import React, { useState } from 'react';
import { X, Save, User, Package, Truck, AlertCircle } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'En attente' },
  { value: 'scheduled', label: 'Planifié' },
  { value: 'assigned', label: 'Affecté' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'collected', label: 'Déposé' },
  { value: 'cancelled', label: 'Annulé' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Basse' },
  { value: 'normal', label: 'Normale' },
  { value: 'high', label: 'Haute' },
  { value: 'urgent', label: 'Urgente' },
];

export default function DepotEditModal({ deposit, drivers, onClose, onSave }) {
  const [form, setForm] = useState({
    client_name: deposit?.client_name || '',
    client_phone: deposit?.client_phone || '',
    client_address: deposit?.client_address || '',
    client_city: deposit?.client_city || '',
    quantity: deposit?.quantity || 1,
    planned_collection_date: deposit?.planned_collection_date || '',
    priority: deposit?.priority || 'normal',
    assigned_driver_id: deposit?.assigned_driver_id || '',
    collection_status: deposit?.collection_status || 'pending',
    delivery_notes: deposit?.delivery_notes || '',
    admin_comment: deposit?.admin_comment || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form?.client_name?.trim()) {
      setError('Le nom du client est requis.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(deposit?.id, form);
      onClose();
    } catch (err) {
      setError(err?.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Modifier le Dépôt</h2>
              <p className="text-sm text-gray-500">#{deposit?.id?.slice(0, 8)?.toUpperCase()}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Client Info */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Informations Client
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du client *</label>
                <input
                  type="text"
                  value={form?.client_name}
                  onChange={(e) => handleChange('client_name', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={form?.client_phone}
                  onChange={(e) => handleChange('client_phone', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input
                  type="text"
                  value={form?.client_address}
                  onChange={(e) => handleChange('client_address', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                <input
                  type="text"
                  value={form?.client_city}
                  onChange={(e) => handleChange('client_city', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>

          {/* Deposit Details */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Détails du Dépôt
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantité</label>
                <input
                  type="number"
                  min="1"
                  value={form?.quantity}
                  onChange={(e) => handleChange('quantity', parseInt(e?.target?.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date prévue</label>
                <input
                  type="date"
                  value={form?.planned_collection_date}
                  onChange={(e) => handleChange('planned_collection_date', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                <select
                  value={form?.priority}
                  onChange={(e) => handleChange('priority', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                >
                  {PRIORITY_OPTIONS?.map(opt => (
                    <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={form?.collection_status}
                  onChange={(e) => handleChange('collection_status', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                >
                  {STATUS_OPTIONS?.map(opt => (
                    <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Driver */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Chauffeur
            </h3>
            <select
              value={form?.assigned_driver_id}
              onChange={(e) => handleChange('assigned_driver_id', e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            >
              <option value="">Non assigné</option>
              {drivers?.map(driver => (
                <option key={driver?.id} value={driver?.id}>{driver?.name}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes de livraison</label>
            <textarea
              value={form?.delivery_notes}
              onChange={(e) => handleChange('delivery_notes', e?.target?.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              placeholder="Instructions spéciales, accès, etc."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire admin</label>
            <textarea
              value={form?.admin_comment}
              onChange={(e) => handleChange('admin_comment', e?.target?.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              placeholder="Commentaire interne..."
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-60"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-60"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Enregistrer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
